import React, { useState } from "react";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSChip from "../../system-design/DSChip";
import DSAccordion from "../../system-design/DSAccordion";
import DSAccordionSummary from "../../system-design/DSAccordionSummary";
import DSAccordionDetails from "../../system-design/DSAccordionDetails";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import { ExpandMore } from "@mui/icons-material";
import useCalendarEvents from "../hooks/useCalendarEvents";
import useLoadKlydos from "../hooks/useLoadKlydos";
import { groupEventsByCountry } from "../../../utils/calendarEventPriority";
import CalendarEventDialog from "./CalendarEventDialog";
import CountryEventCard from "./CountryEventCard";
import { PopulatedCalendarEvent } from "../../../api/calendarEvent";

const WeekEventsTab = () => {
  const [selectedEvent, setSelectedEvent] =
    useState<PopulatedCalendarEvent | null>(null);

  const getDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        date,
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      });
    }
    return dates;
  };

  const dates = getDates();

  const eventsQueries = dates.map((d) =>
    useCalendarEvents({
      year: d.year,
      month: d.month,
      day: d.day,
    }),
  );

  const klydosQueries = dates.map((d) =>
    useLoadKlydos({
      year: d.year,
      month: d.month,
      day: d.day,
    }),
  );

  const isLoading = eventsQueries.some((q) => q.isLoading);
  const hasError = eventsQueries.some((q) => q.error);

  const handleEventClick = (event: PopulatedCalendarEvent) => {
    setSelectedEvent(event);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
  };

  const handleLoadAllKlydos = (dayIndex: number) => {
    klydosQueries[dayIndex].handleLoadKlydos();
  };

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (hasError) {
    return (
      <DSBox sx={{ textAlign: "center", py: 4 }}>
        <DSTypography variant="h6" color="error" gutterBottom>
          Error loading events
        </DSTypography>
        <DSTypography variant="body2" color="text.secondary">
          Unable to fetch calendar events for the week
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox>
      <DSTypography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Events for the Next 7 Days
      </DSTypography>

      {dates.map((dateInfo, dayIndex) => {
        const dayEvents =
          eventsQueries[dayIndex].data?.map((event) => ({
            ...event,
            klydos: [],
          })) || [];

        const eventsByCountry = groupEventsByCountry(dayEvents);
        const hasEvents = dayEvents.length > 0;

        const dayLabel = dateInfo.date.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        const isToday = dayIndex === 0;
        const isAllKlydosLoaded = klydosQueries[dayIndex].klydosLoaded;
        const isAllKlydosLoading = klydosQueries[dayIndex].klydosLoading;

        return (
          <DSAccordion
            key={dayIndex}
            defaultExpanded={isToday}
            sx={{
              mb: 2,
              border: "1px solid",
              borderColor: "divider",
              "&:before": { display: "none" },
            }}
          >
            <DSAccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: isToday
                  ? "rgba(255, 216, 72, 0.12)"
                  : "white.5",
                "&:hover": {
                  backgroundColor: isToday
                    ? "rgba(255, 216, 72, 0.20)"
                    : "white.12",
                },
              }}
            >
              <DSBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  pr: 2,
                }}
              >
                <DSTypography variant="h6">
                  {dayLabel}
                  {isToday && (
                    <DSChip
                      label="TODAY"
                      color="primary"
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  )}
                </DSTypography>
                <DSChip
                  label={
                    hasEvents
                      ? `${dayEvents.length} event${dayEvents.length > 1 ? "s" : ""}`
                      : "No events"
                  }
                  size="small"
                  color={hasEvents ? "default" : "default"}
                  variant="outlined"
                />
              </DSBox>
            </DSAccordionSummary>
            <DSAccordionDetails>
              {!hasEvents ? (
                <DSTypography variant="body2" color="text.secondary">
                  No events scheduled for this day
                </DSTypography>
              ) : (
                <DSBox>
                  <DSBox
                    sx={{
                      mb: 3,
                      pb: 2,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <DSPrimaryButton
                      onClick={() => handleLoadAllKlydos(dayIndex)}
                      disabled={isAllKlydosLoading || isAllKlydosLoaded}
                    >
                      {isAllKlydosLoading
                        ? "Loading All Klydos..."
                        : isAllKlydosLoaded
                          ? "All Klydos Loaded"
                          : "Load All Klydos for All Countries"}
                    </DSPrimaryButton>
                  </DSBox>

                  {Object.entries(eventsByCountry).map(
                    ([countryName, countryEvents]) => {
                      const countryCode = countryEvents[0]?.country?.code || "";

                      return (
                        <CountryEventCard
                          key={countryName}
                          countryName={countryName}
                          countryCode={countryCode}
                          events={countryEvents}
                          year={dateInfo.year}
                          month={dateInfo.month}
                          day={dateInfo.day}
                          allKlydosData={
                            isAllKlydosLoaded
                              ? klydosQueries[dayIndex].populatedEvents
                              : undefined
                          }
                          onEventClick={handleEventClick}
                        />
                      );
                    },
                  )}
                </DSBox>
              )}
            </DSAccordionDetails>
          </DSAccordion>
        );
      })}

      {selectedEvent && (
        <CalendarEventDialog
          open={!!selectedEvent}
          onClose={handleCloseModal}
          calendarEvent={selectedEvent}
        />
      )}
    </DSBox>
  );
};

export default WeekEventsTab;
